import { FC } from 'react';
import { FormikProps } from 'formik';
import { useAuthStore } from 'state/auth';

interface ReviewStepProps {
  form: FormikProps<{
    category: string;
    details: string;
    assigneeType: 'group' | 'user';
    assigneeId: number;
    assigneeName: string;
  }>;
}

const ReviewStep: FC<ReviewStepProps> = ({ form }) => {
  const currentUser = useAuthStore(state => state.user);

  const categoryLabel =
    {
      hardware: 'Hardware',
      system_access: 'System Access',
      software: 'Software',
      email: 'Email Account',
      communication: 'Communication Tools',
      training: 'Training Access',
      other: 'Other',
    }[form.values.category] || form.values.category;

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Review Your Request</h3>
        <p className="text-sm text-gray-600 mb-6">Please review the details below before submitting your request.</p>

        <dl className="space-y-4">
          <div className="border-b border-gray-200 pb-4">
            <dt className="text-sm font-medium text-gray-500">Requester</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {currentUser?.fullName || `${currentUser?.firstName} ${currentUser?.lastName}`}
              <span className="text-gray-500 ml-2">({currentUser?.email})</span>
            </dd>
          </div>

          <div className="border-b border-gray-200 pb-4">
            <dt className="text-sm font-medium text-gray-500">Request Category</dt>
            <dd className="mt-1 text-sm text-gray-900">{categoryLabel}</dd>
          </div>

          <div className="border-b border-gray-200 pb-4">
            <dt className="text-sm font-medium text-gray-500">Request Details</dt>
            <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{form.values.details}</dd>
          </div>

          <div className="border-b border-gray-200 pb-4">
            <dt className="text-sm font-medium text-gray-500">Assigned To</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {form.values.assigneeName}
              <span className="ml-2 inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                {form.values.assigneeType === 'group' ? 'Group' : 'Individual'}
              </span>
            </dd>
          </div>
        </dl>
      </div>

      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              Once submitted, an email notification will be sent to the assignee with a link to view this request.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;
